import { useEffect, useRef, useState } from "react";

interface SharePanelProps {
  title: string;
  url: string;
}

export default function SharePanel({ title, url }: SharePanelProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const area = document.createElement("textarea");
        area.value = url;
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        document.execCommand("copy");
        document.body.removeChild(area);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(`${title} - 御书屋`);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
      >
        <i className="ri-share-forward-line"></i>
        分享
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-64 rounded-lg border border-background-300 bg-background-50 p-3">
          <p className="mb-2 text-xs text-foreground-500">分享链接</p>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-2 py-1.5">
            <input
              readOnly
              value={url}
              className="w-full bg-transparent text-xs text-foreground-600 outline-none"
            />
            <button
              type="button"
              onClick={copyLink}
              className="whitespace-nowrap rounded bg-accent-500 px-2 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              {copied ? "已复制" : "复制"}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <a
              href={`https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-1 rounded-md bg-background-100 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-200"
            >
              <i className="ri-weibo-line"></i>
              微博
            </a>
            <a
              href={`https://connect.qq.com/widget/shareqq/index.html?url=${encodedUrl}&title=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-1 rounded-md bg-background-100 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-200"
            >
              <i className="ri-qq-line"></i>
              QQ
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-1 rounded-md bg-background-100 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-200"
            >
              <i className="ri-twitter-x-line"></i>
              X
            </a>
          </div>
          {copied && <p className="mt-2 text-xs text-accent-600">链接已复制到剪贴板</p>}
        </div>
      )}
    </div>
  );
}